import React, { useState, useEffect } from 'react';
import { myAxios } from '../services/Helper';
import './admin.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faIndianRupeeSign, faPlus, faChevronDown } from '@fortawesome/free-solid-svg-icons';

const ManageFunctions = () => {
    const [functions, setFunctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        description: '',
        price: ''
    });

    useEffect(() => {
        fetchFunctions();
    }, []);

    const fetchFunctions = async () => {
        try {
            const response = await myAxios.get('/function');
            setFunctions(response.data.map(func => ({
                id: func.functionId,
                name: func.functionName,
                description: func.functionDesc,
                price: func.functionPrice,
                addedDate: new Date().toLocaleDateString()
            })));
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddFunction = async (e) => {
        e.preventDefault();
        try {
            if (formData.id) {
                // Update existing function
                const updatedFunction = {
                    functionName: formData.name,
                    functionDesc: formData.description,
                    functionPrice: parseFloat(formData.price)
                };
                await myAxios.put(`/function/${formData.id}`, updatedFunction);
            } else {
                // Add new function
                const newFunction = {
                    functionName: formData.name,
                    functionDesc: formData.description,
                    functionPrice: parseFloat(formData.price)
                };
                await myAxios.post('/addfunction', newFunction);
            }

            setFormData({ id: '', name: '', description: '', price: '' });
            setShowForm(false);
            fetchFunctions();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add function. Please check your permissions.');
        }
    };

    const handleDeleteFunction = async (functionId) => {
        if (window.confirm('Are you sure you want to delete this function?')) {
            try {
                await myAxios.delete(`/function/${functionId}`);
                fetchFunctions();
            } catch (err) {
                setError(err.message);
            }
        }
    };

    const handleEditFunction = (functionId) => {
        const func = functions.find(f => f.id === functionId);
        if (func) {
            setFormData({
                id: func.id,
                name: func.name,
                description: func.description,
                price: func.price.toString()
            });
            setShowForm(true);
        }
    };

    const navigateToSubFunctions = () => {
        window.location.href = '/admin/events/subfunctions';
    };

    if (loading) return <div className="loading">Loading functions...</div>;
    if (error) return <div className="error">Error: {error}</div>;

    return (
        <div className="functions-container">
            <div className="functions-header">
                <h2>Lawn Rental Management</h2>
                <button className="add-function-btn" onClick={() => setShowForm(true)}>
                    <FontAwesomeIcon icon={faPlus} /> Add Function
                </button>
            </div>

            {showForm && (
                <div className="form-overlay">
                    <div className="form-container">
                        <h3>{formData.id ? 'Edit' : 'Add New'} Function</h3>
                        <form onSubmit={handleAddFunction}>
                            <input
                                type="text"
                                name="name"
                                placeholder="Function Name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                className="form-input"
                            />
                            <textarea
                                name="description"
                                placeholder="Function Description"
                                value={formData.description}
                                onChange={handleInputChange}
                                required
                                className="form-textarea"
                            ></textarea>
                            <input
                                type="number"
                                name="price"
                                placeholder="Price"
                                value={formData.price}
                                onChange={handleInputChange}
                                required
                                className="form-input"
                            />
                            <div className="form-buttons">
                                <button type="submit" className="btn-primary">
                                    {formData.id ? 'Update' : 'Add'} Function
                                </button>
                                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="functions-grid">
                {functions.length === 0 ? (
                    <div className="empty-state">
                        No functions found. Add one to get started.
                    </div>
                ) : (
                    functions.map(func => (
                        <div key={func.id} className="function-card">
                            <div className="function-header">
                                <h3>{func.name}</h3>
                                <span className="date-added">Added: {func.addedDate}</span>
                            </div>
                            <p className="function-description">{func.description}</p>
                            <div className="function-price">
                                <FontAwesomeIcon icon={faIndianRupeeSign} /> {func.price.toFixed(2)}
                            </div>
                            <div className="function-actions">
                                <button
                                    className="action-btn edit-btn"
                                    title="Edit"
                                    onClick={() => handleEditFunction(func.id)}
                                >
                                    <FontAwesomeIcon icon={faPencil} />
                                </button>
                                <button
                                    className="action-btn delete-btn"
                                    title="Delete"
                                    onClick={() => handleDeleteFunction(func.id)}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                                <button
                                    className="action-btn price-btn"
                                    title="Price details"
                                >
                                    <FontAwesomeIcon icon={faIndianRupeeSign} />
                                </button>
                                <button
                                    className="action-btn add-item-btn"
                                    title="Add Sub-Function"
                                    onClick={navigateToSubFunctions}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ManageFunctions;